// ============================================
// Legacy fetch hooks - DEPRECATED
// Use services directly instead (tmdbService, youtubeService)
// ============================================

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios, { AxiosRequestConfig } from 'axios';
import { TMDB_API_KEY, TMDB_BASE_URL } from '../config/constants';

interface FetchState<T> {
    data: T | null;
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
}

/**
 * Generic fetch hook with loading, error, and data states
 * @deprecated Use services directly: tmdbService, youtubeService
 */
export const useFetch = <T = unknown>(url: string, options: AxiosRequestConfig = {}): FetchState<T> => {
    const [data, setData] = useState<T | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const optionsRef = useRef(options);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await axios.get<T>(url, optionsRef.current);
            setData(response.data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    }, [url]);

    useEffect(() => {
        if (url) {
            fetchData();
        }
    }, [url, fetchData]);

    return { data, loading, error, refetch: fetchData };
};

/**
 * Build TMDB URL helper (stable reference)
 */
const buildTMDBUrl = (endpoint: string, params: Record<string, string | number> = {}): string => {
    const queryParams = new URLSearchParams({
        api_key: TMDB_API_KEY,
        ...Object.fromEntries(Object.entries(params).map(([k, v]) => [k, String(v)]))
    });
    return `${TMDB_BASE_URL}${endpoint}?${queryParams}`;
};

/**
 * TMDB specific hooks - memoized to prevent re-renders
 * @deprecated Use tmdbService directly from @/services
 */
export const useTMDB = () => {
    const fetchPopularMovies = useCallback(async (page = 1) => {
        const response = await axios.get(buildTMDBUrl('/movie/popular', { page }));
        return response.data.results.map((movie: any) => ({ ...movie, media_type: 'movie' }));
    }, []);

    const fetchPopularTV = useCallback(async (page = 1) => {
        const response = await axios.get(buildTMDBUrl('/tv/popular', { page }));
        return response.data.results.map((show: any) => ({ ...show, media_type: 'tv' }));
    }, []);

    const fetchTrending = useCallback(async (timeWindow: 'day' | 'week' = 'week') => {
        const response = await axios.get(buildTMDBUrl(`/trending/all/${timeWindow}`));
        return response.data.results;
    }, []);

    const searchMulti = useCallback(async (query: string) => {
        if (!query.trim()) return [];
        const response = await axios.get(buildTMDBUrl('/search/multi', { query }));
        return response.data.results.filter((item: any) =>
            item.media_type === 'movie' || item.media_type === 'tv'
        );
    }, []);

    const fetchMediaDetails = useCallback(async (type: 'movie' | 'tv', id: number) => {
        const response = await axios.get(
            buildTMDBUrl(`/${type}/${id}`, { append_to_response: 'credits,videos,similar,recommendations' })
        );
        return response.data;
    }, []);

    const fetchSeasonDetails = useCallback(async (tvId: number, seasonNumber: number) => {
        const response = await axios.get(buildTMDBUrl(`/tv/${tvId}/season/${seasonNumber}`));
        return response.data;
    }, []);

    const fetchActorCredits = useCallback(async (actorId: number) => {
        const response = await axios.get(buildTMDBUrl(`/person/${actorId}/combined_credits`));
        return response.data.cast;
    }, []);

    const fetchTrailer = useCallback(async (type: 'movie' | 'tv', id: number) => {
        const response = await axios.get(buildTMDBUrl(`/${type}/${id}/videos`));
        const trailers = response.data.results?.filter(
            (video: any) => video.type === 'Trailer' && video.site === 'YouTube'
        );
        return trailers?.[0]?.key || null;
    }, []);

    // Return memoized object to prevent reference changes
    return useMemo(() => ({
        fetchPopularMovies,
        fetchPopularTV,
        fetchTrending,
        searchMulti,
        fetchMediaDetails,
        fetchSeasonDetails,
        fetchActorCredits,
        fetchTrailer,
    }), [
        fetchPopularMovies, fetchPopularTV, fetchTrending, searchMulti,
        fetchMediaDetails, fetchSeasonDetails, fetchActorCredits, fetchTrailer
    ]);
};

/**
 * YouTube videos hook
 * @deprecated Use youtubeService.fetchChannelVideos() from @/services
 */
export const useYouTubeVideos = (channelId: string) => {
    const [videos, setVideos] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchVideos = async () => {
            try {
                setLoading(true);
                const { youtubeService } = await import('../services');
                const data = await youtubeService.fetchChannelVideos(channelId);
                setVideos(data);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load videos');
            } finally {
                setLoading(false);
            }
        };

        if (channelId) {
            fetchVideos();
        }
    }, [channelId]);

    return { videos, loading, error };
};

export default useFetch;
